import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Button, Tag, Tooltip, Empty, Spin, Dropdown, Segmented, App as AntdApp,
} from 'antd';
import {
  Plus, MoreVertical, Archive, Trash2, Calendar, ChevronDown, ChevronRight,
  LayoutList, LayoutGrid, Pencil, Users, History as HistoryIcon,
} from 'lucide-react';
import dayjs from 'dayjs';
import { projectService, type ProjectListItem, type ProjectTaskListItem } from '@/services/projectService';
import { ProjectMembersModal } from './ProjectMembersModal';
import { ProjectAuditLogModal } from './ProjectAuditLogModal'; // V1.0.65

// Helper: viết tắt tên người (vd "Đỗ Hữu Ích" → "ĐHI")
function getInitials(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return parts.map(p => p[0]?.toUpperCase() ?? '').join('').slice(0, 4);
}

// Format due date kiểu wireframe "19/5"
function formatShortDate(date: string | null | undefined): string {
  if (!date) return '';
  const d = dayjs(date);
  return `${d.format('D')}/${d.format('M')}`;
}

// Thứ tự hiển thị: Chưa làm → Đang thực hiện → Chờ duyệt → Hoàn thành → Quá hạn → Đã hủy
const STATUS_GROUPS: { key: string; label: string; statuses: string[]; color: string; bg: string }[] = [
  { key: 'todo',        label: 'CHƯA LÀM',       statuses: ['NEW', 'ASSIGNED', 'TODO', 'OPEN'],   color: '#5b6875', bg: '#f0f4f8' },
  { key: 'in_progress', label: 'ĐANG THỰC HIỆN', statuses: ['IN_PROGRESS'],                      color: '#1976D2', bg: '#e6f4ff' },
  { key: 'review',      label: 'CHỜ DUYỆT',      statuses: ['PENDING_REVIEW'],                    color: '#fa8c16', bg: '#fff7e6' },
  { key: 'completed',   label: 'HOÀN THÀNH',     statuses: ['COMPLETED', 'DONE'],                 color: '#52c41a', bg: '#e6ffed' },
  { key: 'overdue',     label: 'QUÁ HẠN',         statuses: ['OVERDUE'],                           color: '#f5222d', bg: '#fff1f0' },
  { key: 'cancelled',   label: 'ĐÃ HỦY',          statuses: ['CANCELLED'],                         color: '#bdc4cd', bg: '#f5f5f5' },
];

interface Props {
  projectListItem: ProjectListItem;
  deptName?: string | null;
  onArchive: () => void;
  onDelete: () => void;
  /** V1.0.36: Callback khi user bấm "Sửa" — parent mở EditProjectModal */
  onEdit?: () => void;
  /** V1.0.36: User có quyền sửa project này không (owner/manager/admin) */
  canEdit?: boolean;
}

type ViewMode = 'list' | 'kanban';
const VIEW_MODE_STORAGE_KEY = 'hv-project-view-mode';

export function ProjectDetailPanel({ projectListItem, deptName, onArchive, onDelete, onEdit, canEdit }: Props) {
  const { message, modal } = AntdApp.useApp();
  const navigate = useNavigate();

  // V1.0.34: Load project detail để có sharedWithDepartments
  const { data: projectDetail } = useQuery({
    queryKey: ['project-detail', projectListItem.id],
    queryFn: () => projectService.getById(projectListItem.id),
    enabled: !!projectListItem.id,
  });
  const sharedDepts = projectDetail?.sharedWithDepartments ?? [];

  // View mode persisted to localStorage
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    return (saved === 'kanban' || saved === 'list') ? saved : 'list';
  });
  useEffect(() => {
    localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
  }, [viewMode]);

  // V1.0.35: Bỏ drag-drop + status mutation — chỉ đổi trạng thái trong chi tiết task

  // Click task → navigate sang task detail page
  const handleTaskClick = (taskId: string) => {
    navigate(`/tasks/${taskId}`);
  };

  // State: section nào đang collapse (chỉ dùng cho list view)
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const toggleCollapse = (key: string) => {
    setCollapsed(s => ({ ...s, [key]: !s[key] }));
  };

  // V1.0.64: Modal quản lý thành viên dự án
  const [membersModalOpen, setMembersModalOpen] = useState(false);
  // V1.0.65: Modal xem lịch sử thay đổi dự án
  const [auditModalOpen, setAuditModalOpen] = useState(false);

  // Detail (members, etc)
  const { data: detail } = useQuery({
    queryKey: ['project-detail', projectListItem.id],
    queryFn: () => projectService.getById(projectListItem.id),
  });

  // Tasks trong project
  const { data: tasks = [], isLoading: tasksLoading } = useQuery({
    queryKey: ['project-tasks', projectListItem.id],
    queryFn: () => projectService.getTasks(projectListItem.id),
  });

  // Group tasks theo status
  const groupedTasks = useMemo(() => {
    const groups: Record<string, ProjectTaskListItem[]> = {};
    for (const g of STATUS_GROUPS) groups[g.key] = [];
    for (const t of tasks) {
      const grp = STATUS_GROUPS.find(g => g.statuses.includes(t.status));
      if (grp) groups[grp.key].push(t);
    }
    return groups;
  }, [tasks]);

  // Stats
  const stats = useMemo(() => {
    const total = tasks.length;
    const inProgress = tasks.filter(t => t.status === 'IN_PROGRESS').length;
    const completed = tasks.filter(t => t.status === 'COMPLETED' || t.status === 'DONE').length;
    return { total, inProgress, completed };
  }, [tasks]);

  // Navigate sang TaskFormPage với projectId pre-filled
  const handleAddTask = () => {
    navigate('/tasks/new', {
      state: {
        projectId: projectListItem.id,
        projectName: projectListItem.name,
        departmentId: projectListItem.departmentId,
      },
    });
  };

  const hasTasks = tasks.length > 0;

  const moreMenu = {
    items: [
      // V1.0.36: Sửa dự án — chỉ owner/manager/admin
      ...(canEdit && onEdit ? [
        {
          key: 'edit', icon: <Pencil size={14} />, label: 'Sửa dự án',
          onClick: onEdit,
        },
      ] : []),
      // V1.0.64: Quản lý thành viên — ai cũng vào xem được, nhưng chỉ canEdit mới sửa được trong modal
      {
        key: 'members', icon: <Users size={14} />, label: 'Quản lý thành viên',
        onClick: () => setMembersModalOpen(true),
      },
      // V1.0.65: Lịch sử thay đổi dự án (audit log)
      {
        key: 'audit', icon: <HistoryIcon size={14} />, label: 'Lịch sử thay đổi',
        onClick: () => setAuditModalOpen(true),
      },
      { type: 'divider' as const, key: 'div-members' },
      {
        key: 'archive', icon: <Archive size={14} />, label: 'Lưu trữ',
        onClick: () => {
          modal.confirm({
            title: 'Lưu trữ dự án?',
            content: hasTasks
              ? `Dự án có ${tasks.length} task. Sẽ ẩn khỏi danh sách. Có thể khôi phục sau ở tab "Lưu trữ".`
              : 'Dự án sẽ ẩn khỏi danh sách. Có thể khôi phục sau ở tab "Lưu trữ".',
            onOk: onArchive,
          });
        },
      },
      { type: 'divider' as const },
      {
        key: 'delete', icon: <Trash2 size={14} />,
        label: hasTasks
          ? <Tooltip title="Dự án có task — chỉ được lưu trữ, không xóa được">
              <span>Xóa dự án (đã khóa)</span>
            </Tooltip>
          : 'Xóa dự án',
        danger: !hasTasks,
        disabled: hasTasks,
        onClick: () => {
          if (hasTasks) {
            message.warning('Dự án đã có task — không thể xóa. Hãy lưu trữ thay vì xóa.');
            return;
          }
          modal.confirm({
            title: 'Xóa dự án?',
            content: 'Hành động này KHÔNG THỂ hoàn tác. Chỉ áp dụng cho dự án chưa có task.',
            okType: 'danger',
            onOk: onDelete,
          });
        },
      },
    ],
  };

  return (
    <div style={{
      height: '100%', display: 'flex', flexDirection: 'column',
      background: '#fff',
    }}>
      {/* ============ HEADER ============ */}
      <div className="hv-project-detail-header" style={{ padding: '14px 20px 10px', borderBottom: '1px solid #f0f4f8' }}>
        {/* Breadcrumb + actions */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
            {deptName && (
              <>
                <span style={{ color: '#7d8a98' }}>{deptName}</span>
                <ChevronRight size={14} color="#bdc4cd" />
              </>
            )}
            <span style={{ fontWeight: 700, color: '#1a1e23', fontSize: 16 }}>
              {projectListItem.name}
            </span>
            <Tag style={{ marginLeft: 8 }} color={
              projectListItem.status === 'COMPLETED' ? 'success' :
              projectListItem.status === 'ARCHIVED' ? 'default' :
              projectListItem.status === 'ON_HOLD' ? 'warning' : 'processing'
            }>
              {projectListItem.status}
            </Tag>
            <Tag color={projectListItem.workflowMode === 'SEQUENTIAL' ? 'purple' : 'cyan'}>
              {projectListItem.workflowMode === 'SEQUENTIAL' ? 'Tuần tự' : 'Song song'}
            </Tag>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {/* 3 stat chips kiểu wireframe */}
            <StatChip count={stats.total} label="task" bg="#e6f4ff" color="#1976D2" />
            <StatChip count={stats.inProgress} label="đang làm" bg="#fff7e6" color="#fa8c16" />
            <StatChip count={stats.completed} label="xong" bg="#e6ffed" color="#52c41a" />

            {/* View mode toggle */}
            <Segmented
              size="small"
              value={viewMode}
              onChange={(v) => setViewMode(v as ViewMode)}
              options={[
                { value: 'list', icon: <LayoutList size={14} />, label: '' },
                { value: 'kanban', icon: <LayoutGrid size={14} />, label: '' },
              ]}
              style={{ marginLeft: 8 }}
            />

            <Dropdown menu={moreMenu} trigger={['click']}>
              <Button type="text" icon={<MoreVertical size={16} />} />
            </Dropdown>
          </div>
        </div>

        {/* V1.0.34: Khoa được xem dự án (multi-dept) */}
        {sharedDepts.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
            <span style={{ fontSize: 11, color: '#7d8a98', fontWeight: 500 }}>Khoa được xem:</span>
            {sharedDepts.map((d, i) => (
              <Tag
                key={d.id}
                color={i === 0 ? 'blue' : undefined}
                style={{
                  margin: 0,
                  fontSize: 11,
                  ...(d.groupColor && i !== 0 ? {
                    background: `${d.groupColor}15`,
                    borderColor: `${d.groupColor}40`,
                    color: d.groupColor,
                  } : {}),
                }}
              >
                {d.name}
                {i === 0 && <span style={{ marginLeft: 4, fontSize: 10, opacity: 0.85 }}>· chủ quản</span>}
              </Tag>
            ))}
          </div>
        )}

        {/* Progress bar full width */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 12, color: '#5b6875', minWidth: 80 }}>Tiến độ nhóm</span>
          <div style={{
            flex: 1, height: 6, background: '#f0f4f8', borderRadius: 3, overflow: 'hidden',
          }}>
            <div style={{
              width: `${projectListItem.progressPct}%`, height: '100%',
              background: 'linear-gradient(90deg, #1976D2 0%, #52c41a 100%)',
              transition: 'width 0.3s',
            }} />
          </div>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#1a1e23', minWidth: 35, textAlign: 'right' }}>
            {projectListItem.progressPct}%
          </span>
        </div>

        {/* V1.0.64: Members - gộp avatars + danh sách họ tên trong 1 dòng inline */}
        {detail && detail.members.length > 0 && (
          <div style={{
            marginTop: 10,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: 12, color: '#7d8a98', whiteSpace: 'nowrap', paddingTop: 4 }}>
              Thành viên ({detail.members.length}):
            </span>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
              flex: 1,
              minWidth: 0,
              maxHeight: 120,
              overflowY: 'auto',
            }}>
              {detail.members.map(m => {
                const roleLabel =
                  m.role === 'OWNER' ? 'Chủ dự án' :
                  m.role === 'MANAGER' ? 'Quản trị' :
                  m.role === 'VIEWER' ? 'Chỉ xem' :
                  null; // MEMBER không hiện badge
                const roleColor =
                  m.role === 'OWNER' ? '#ed8936' :
                  m.role === 'MANAGER' ? '#1976D2' :
                  m.role === 'VIEWER' ? '#805ad5' :
                  '#5b6875';
                return (
                  <span
                    key={m.userId}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      fontSize: 12,
                      padding: '2px 8px 2px 3px',
                      background: '#fff',
                      border: '1px solid #e0e6eb',
                      borderRadius: 14,
                      color: '#1a1e23',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {/* Avatar mini inline */}
                    <span style={{
                      width: 20, height: 20, borderRadius: '50%',
                      background: '#1976D2', color: '#fff',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 10, fontWeight: 600,
                      flexShrink: 0,
                    }}>
                      {getInitials(m.fullName)}
                    </span>
                    <span>{m.fullName}</span>
                    {roleLabel && (
                      <span style={{
                        fontSize: 10,
                        color: roleColor,
                        fontWeight: 600,
                      }}>
                        · {roleLabel}
                      </span>
                    )}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ============ TASK LIST grouped by status ============ */}
      <div className="hv-project-task-list" style={{ flex: 1, overflowY: 'auto', padding: '12px 20px 20px', background: '#fafbfc' }}>
        {tasksLoading && (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin />
          </div>
        )}

        {!tasksLoading && tasks.length === 0 && (
          <Empty
            description="Dự án chưa có task nào"
            style={{ marginTop: 60 }}
          />
        )}

        {!tasksLoading && tasks.length > 0 && viewMode === 'list' && STATUS_GROUPS.map(grp => {
          const groupTasks = groupedTasks[grp.key] ?? [];
          if (groupTasks.length === 0) return null;
          const isCollapsed = collapsed[grp.key];

          return (
            <div key={grp.key} style={{ marginBottom: 20 }}>
              {/* Section header */}
              <div
                onClick={() => toggleCollapse(grp.key)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '6px 10px', marginBottom: 6,
                  cursor: 'pointer', userSelect: 'none',
                }}
              >
                {isCollapsed
                  ? <ChevronRight size={14} color="#7d8a98" />
                  : <ChevronDown size={14} color="#7d8a98" />}
                <span style={{
                  fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
                  color: grp.color, textTransform: 'uppercase',
                }}>
                  {grp.label} ({groupTasks.length})
                </span>
              </div>

              {/* Task cards */}
              {!isCollapsed && groupTasks.map(t => (
                <TaskCard
                  key={t.id}
                  task={t}
                  groupColor={grp.color}
                  groupBg={grp.bg}
                  onClick={() => handleTaskClick(t.id)}
                />
              ))}
            </div>
          );
        })}

        {/* Kanban view - V1.0.35: bỏ drag-drop, chỉ click mở chi tiết */}
        {!tasksLoading && tasks.length > 0 && viewMode === 'kanban' && (
          <div className="hv-kanban-grid" style={{
            display: 'grid',
            gridTemplateColumns: `repeat(4, minmax(220px, 1fr))`,
            gap: 12,
            paddingBottom: 8,
            overflowX: 'auto',
          }}>
            {STATUS_GROUPS.filter(g => ['todo', 'in_progress', 'review', 'completed'].includes(g.key)).map(grp => {
              const groupTasks = groupedTasks[grp.key] ?? [];
              return (
                <div
                  key={grp.key}
                  style={{
                    background: '#fff',
                    border: '1px solid #e8edf2',
                    borderTop: `3px solid ${grp.color}`,
                    borderRadius: 8,
                    padding: 8,
                    minHeight: 200,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                    transition: 'all 0.15s',
                  }}>
                  {/* Column header */}
                  <div style={{
                    padding: '4px 8px',
                    fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
                    color: grp.color, textTransform: 'uppercase',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}>
                    <span>{grp.label}</span>
                    <span style={{
                      background: grp.bg, color: grp.color,
                      borderRadius: 10, padding: '0 8px',
                      fontSize: 11, minWidth: 22, textAlign: 'center',
                    }}>{groupTasks.length}</span>
                  </div>

                  {/* Tasks in column - V1.0.35: bỏ drag-drop, chỉ mở chi tiết */}
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {groupTasks.length === 0 ? (
                      <div style={{
                        padding: 20, textAlign: 'center',
                        fontSize: 11, color: '#bdc4cd',
                      }}>
                        Trống
                      </div>
                    ) : groupTasks.map(t => (
                      <TaskCard
                        key={t.id}
                        task={t}
                        groupColor={grp.color}
                        groupBg={grp.bg}
                        compact
                        onClick={() => handleTaskClick(t.id)}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Add task button - bay ra form đầy đủ */}
        {!tasksLoading && (
          <div
            onClick={handleAddTask}
            style={{
              background: '#fff', border: '1px dashed #d4d9df', borderRadius: 8,
              padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10,
              cursor: 'pointer', marginTop: 10,
              color: '#5b6875', fontSize: 14,
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = '#1976D2';
              e.currentTarget.style.color = '#1976D2';
              e.currentTarget.style.background = '#f0f7ff';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = '#d4d9df';
              e.currentTarget.style.color = '#5b6875';
              e.currentTarget.style.background = '#fff';
            }}
          >
            <Plus size={16} />
            <span>Thêm task cho <strong>{projectListItem.name}</strong>...</span>
          </div>
        )}
      </div>

      {/* V1.0.64: Modal quản lý thành viên dự án */}
      <ProjectMembersModal
        open={membersModalOpen}
        projectId={projectListItem.id}
        projectName={projectListItem.name}
        canManage={!!canEdit}
        onClose={() => setMembersModalOpen(false)}
      />
      {/* V1.0.65: Modal lịch sử thay đổi dự án */}
      <ProjectAuditLogModal
        open={auditModalOpen}
        projectId={projectListItem.id}
        projectName={projectListItem.name}
        onClose={() => setAuditModalOpen(false)}
      />
    </div>
  );
}

// ============================================================
// Component: stat chip nhỏ
// ============================================================
function StatChip({ count, label, bg, color }: { count: number; label: string; bg: string; color: string }) {
  return (
    <div className="hv-stat-chip" style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      background: bg, color, borderRadius: 14, padding: '3px 10px',
      fontSize: 12, fontWeight: 600,
    }}>
      <span style={{ fontSize: 13 }}>{count}</span>
      <span style={{ fontWeight: 500 }}>{label}</span>
    </div>
  );
}

// ============================================================
// Component: Task card kiểu wireframe
// ============================================================
function TaskCard({
  task, groupColor, compact,
  onClick,
}: {
  task: ProjectTaskListItem;
  groupColor: string;
  groupBg: string;
  compact?: boolean;
  onClick?: () => void;
  // V1.0.35: Bỏ onStatusChange/onDrag — chỉ đổi status trong chi tiết task
}) {
  // V1.0.26: highlight task urgent + important từ cấp 3 trở lên
  const urgentLvl = task.priorityLevel ?? 3;
  const importantLvl = task.importantLevel ?? 3;
  // Border-left ưu tiên mức cao hơn
  const accentColor = urgentLvl >= 4 ? '#cf1322'
                    : importantLvl >= 4 ? '#d46b08'
                    : urgentLvl >= 3 ? '#fa8c16'
                    : importantLvl >= 3 ? '#faad14'
                    : groupColor;
  const accentBg = urgentLvl >= 4 ? '#fff5f5'
                 : importantLvl >= 4 ? '#fffbe6'
                 : '#fff';

  const isCompleted = task.status === 'COMPLETED';

  // Tính số ngày còn lại / quá hạn
  let daysInfo: { text: string; color: string; bg: string } | null = null;
  if (task.dueDate && task.status !== 'COMPLETED' && task.status !== 'CANCELLED') {
    const due = dayjs(task.dueDate).startOf('day');
    const today = dayjs().startOf('day');
    const diffDays = due.diff(today, 'day');
    if (diffDays < 0) {
      daysInfo = { text: `⚠ Quá hạn ${Math.abs(diffDays)} ngày`, color: '#cf1322', bg: '#fff1f0' };
    } else if (diffDays === 0) {
      daysInfo = { text: '🔥 Hết hạn hôm nay', color: '#d46b08', bg: '#fff7e6' };
    } else if (diffDays <= 3) {
      daysInfo = { text: `⏰ Còn ${diffDays} ngày`, color: '#d46b08', bg: '#fff7e6' };
    } else if (diffDays <= 7) {
      daysInfo = { text: `Còn ${diffDays} ngày`, color: '#1976D2', bg: '#e6f4ff' };
    } else {
      daysInfo = { text: `Còn ${diffDays} ngày`, color: '#5b6875', bg: '#f0f4f8' };
    }
  }

  return (
    <div
      onClick={onClick}
      style={{
        background: accentBg,
        border: `1px solid ${urgentLvl >= 4 ? '#ffa39e' : importantLvl >= 4 ? '#ffd591' : '#e8edf2'}`,
        borderLeft: `4px solid ${accentColor}`,
        borderRadius: 6,
        padding: compact ? '8px 10px' : '10px 14px',
        marginBottom: compact ? 0 : 6,
        display: 'flex', alignItems: 'flex-start', gap: 10,
        transition: 'all 0.15s',
        cursor: onClick ? 'pointer' : 'default',
        boxShadow: urgentLvl >= 4 ? '0 1px 3px rgba(207, 19, 34, 0.1)' : 'none',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.boxShadow = urgentLvl >= 4 ? '0 1px 3px rgba(207, 19, 34, 0.1)' : 'none';
      }}
    >
      {/* V1.0.35: Chỉ hiển thị icon status, không cho click toggle */}
      <div
        style={{
          width: 16, height: 16, marginTop: 2,
          border: `2px solid ${isCompleted ? '#52c41a' : '#bdc4cd'}`,
          background: isCompleted ? '#52c41a' : 'transparent',
          borderRadius: 3, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontSize: 11, fontWeight: 700,
          cursor: 'default',
        }}
        title={isCompleted ? 'Đã hoàn thành' : 'Mở task để cập nhật trạng thái'}
      >
        {isCompleted && '✓'}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        {/* V1.0.26: Banner LUÔN hiện cả 2 trục theo format "Gấp: Cấp X · Quan trọng: Cấp Y" */}
        <div style={{
          fontSize: 10, fontWeight: 700, marginBottom: 4,
          display: 'flex', gap: 8, flexWrap: 'wrap',
        }}>
          <span style={{
            color: urgentLvl >= 4 ? '#cf1322' : urgentLvl >= 3 ? '#fa8c16' : '#7d8a98',
          }}>
            🔴 Gấp: Cấp {urgentLvl}
          </span>
          <span style={{ color: '#bdc4cd' }}>·</span>
          <span style={{
            color: importantLvl >= 4 ? '#d46b08' : importantLvl >= 3 ? '#faad14' : '#7d8a98',
          }}>
            🟠 Quan trọng: Cấp {importantLvl}
          </span>
        </div>

        {/* Title */}
        <div style={{
          fontSize: 14, fontWeight: 500,
          color: isCompleted ? '#7d8a98' : '#1a1e23',
          textDecoration: isCompleted ? 'line-through' : 'none',
          marginBottom: 6,
        }}>
          {task.title}
        </div>

        {/* V1.0.26: Days remaining badge - full width nổi bật */}
        {daysInfo && (
          <div style={{
            fontSize: 11, fontWeight: 600,
            color: daysInfo.color,
            background: daysInfo.bg,
            borderRadius: 4,
            padding: '3px 8px',
            marginBottom: 6,
            display: 'inline-flex', alignItems: 'center', gap: 4,
          }}>
            <Calendar size={11} />
            {daysInfo.text}
            {task.dueDate && (
              <span style={{ fontWeight: 400, marginLeft: 4 }}>· {formatShortDate(task.dueDate)}</span>
            )}
          </div>
        )}

        {/* V1.0.35: Người thực hiện + người giao — hiển thị tên đầy đủ */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
          {task.assigneeName && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 10, color: '#7d8a98', minWidth: 56 }}>👤 Thực hiện:</span>
              <span style={{
                fontSize: 11, fontWeight: 600, color: '#1976D2',
              }}>
                {task.assigneeName}
              </span>
            </div>
          )}
          {task.assignerName && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 10, color: '#7d8a98', minWidth: 56 }}>📤 Giao bởi:</span>
              <span style={{
                fontSize: 11, fontWeight: 500, color: '#5b6875',
              }}>
                {task.assignerName}
              </span>
            </div>
          )}
        </div>

        {/* Tags row: blocked + dependency */}
        {(task.isBlocked || task.dependencyCount > 0) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
            {task.isBlocked && (
              <Tooltip title="Đang đợi task khác hoàn thành trước">
                <span style={{
                  fontSize: 10, fontWeight: 600,
                  background: '#fff1f0', color: '#f5222d',
                  borderRadius: 4, padding: '2px 7px',
                }}>
                  ⏳ Đang chờ
                </span>
              </Tooltip>
            )}
            {!task.isBlocked && task.dependencyCount > 0 && (
              <Tooltip title={`Phụ thuộc ${task.dependencyCount} task`}>
                <span style={{
                  fontSize: 10, fontWeight: 600,
                  background: '#f0f4f8', color: '#5b6875',
                  borderRadius: 4, padding: '2px 7px',
                }}>
                  🔗 {task.dependencyCount}
                </span>
              </Tooltip>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
